import * as Sentry from '@sentry/react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useAppContext } from '@contexts/AppContext';

import { useAppSettingsQuery } from '@queries/AppQueries';
import { useCurrentEntertainerQuery } from '@queries/EntertainerQueries';
import {
  useCurrentUser,
  useNonEntertainerDashboardQuery,
  useUserDashboardNotificationsQuery,
} from '@queries/UserQueries';

import { useCapacitorUpdateAvailable } from '@hooks/useCapacitorUpdateAvailable';
import { useContacts } from '@hooks/useContacts';
import { useCitiesQuery, useCityById, useCurrentUserCity } from '@hooks/useCurrentUserLocation';
import { usePWA } from '@hooks/usePWA';

import { useRouter } from '@router/index';

import { createScreenComponent } from '@utilities/createScreenComponent';

import IconButton from '@components/buttons/IconButton';
import { VideoUploadField } from '@components/form/VideoUploadField';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import Card from '@components/layout/Card/Card';
import { Screen } from '@components/layout/Screen';
import { Skeleton } from '@components/layout/Skeleton/Skeleton';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';
import { ReportOverlay } from '@components/overlays/ReportOverlay/ReportOverlay';

import { useInAppNotifications } from '@screens/NotificationsScreen/NotificationsScreen';
import { ChangeCityButton } from '@screens/SettingsScreen';

import { openSplashNotification } from '../../layouts/SplashNotificationPopup/useOpenSplashNotification';
import { ConversationsIcon } from './CommunityScreen';
import './CommunityScreen.scss';
import { NonEntertainerCommunityScreen } from './NonEntertainerCommunityScreen';
import { Featured } from './components/Featured';
import { SuggestedActions } from './components/SuggestedActions';
import { WeeklyMetrics } from './components/WeeklyMetrics';
import { WeeklyScore } from './components/WeeklyScore';

export const NonEntertainerDashboardScreen = createScreenComponent<
  { cityId?: string },
  { cityId?: string }
>(({ f7route, cityId }) => {
  const { navigate } = useRouter();
  const { entertainer } = useCurrentEntertainerQuery();
  const { hasUnreadNotifications } = useInAppNotifications();
  const { currentCityIsReady, currentCity: currentUserCity } = useCurrentUserCity({
    forceFetch: true,
  });
  const { cities } = useCitiesQuery();
  const { overrideCurrentCity } = useAppContext();
  const [supportOverlayIsVisible, setSupportOverlayIsVisible] = useState(false);

  const overrideCityId = f7route.query?.cityId || cityId;
  const { city: currentCity } = useCityById(overrideCityId ?? currentUserCity?._id);

  useEffect(() => {
    if (overrideCityId && cities?.length) {
      const cityOverride = cities.find((city) => city._id === overrideCityId);
      if (cityOverride) {
        overrideCurrentCity(cityOverride, {
          storeInLocalStorage: true,
        });
      }
    }
  }, [overrideCityId, cities]);

  const {
    refetchNonEntertainerDashboard,
    nonEntertainerDashboard,
    nonEntertainerDashboardIsReady,
  } = useNonEntertainerDashboardQuery({
    cityId: currentCity?._id,
  });

  const { refetchUserDashboardNotifications } = useUserDashboardNotificationsQuery({
    cityId: currentCity?._id,
  });

  const handleReenter = () => {
    refetchNonEntertainerDashboard();
    refetchUserDashboardNotifications();
  };

  return (
    <Screen
      name="Non-Entertainer Dashboard Screen"
      disableLocationPermissionGate={false}
      onReenter={handleReenter}
      headerProps={{
        left: (
          <div className="ps-2 d-flex align-items-center" style={{ gap: 10 }}>
            <Screen.GagglTitle />
            {/* <ChangeCityButton
                buttonProps={{
                  variant: 'smooth',
                  style: { padding: '4px 12px' },
                }}
              /> */}
          </div>
        ),
        right: (
          <div className="d-flex gap-2">
            <ConversationsIcon />
            <IconButton
              color="neutral"
              variant="smooth"
              badge={hasUnreadNotifications}
              onClick={() => {
                navigate('/notifications');
              }}
            >
              <FontAwesomeIcon name="bell" />
            </IconButton>
          </div>
        ),
      }}
    >
      <Screen.Content maxWidth={425}>
        <TitleToolbar
          text={<span style={{ fontSize: '1.5rem' }}>Hey sis! 💃🏽</span>}
          className="title-toolbar-welcome mb-0"
          size="md"
        />
        {currentCity?.name ? (
          <p className="mt-0">{`Dive into the ${currentCity?.name} community`}</p>
        ) : (
          <div className="w-100 mb-2">
            <Skeleton height={26} />
          </div>
        )}
        <div className="mb-3">
          <WeeklyScore
            isAppDashboardReady={nonEntertainerDashboardIsReady}
            appDashboard={nonEntertainerDashboard}
          />
        </div>
        <WeeklyMetrics
          isAppDashboardReady={nonEntertainerDashboardIsReady}
          appDashboard={nonEntertainerDashboard}
        />
        <SuggestedActions
          isAppDashboardReady={nonEntertainerDashboardIsReady}
          appDashboard={nonEntertainerDashboard}
          onOpenSupport={() => setSupportOverlayIsVisible(true)}
        />
        <Featured
          isAppDashboardReady={nonEntertainerDashboardIsReady}
          appDashboard={nonEntertainerDashboard}
        />
      </Screen.Content>

      <ReportOverlay
        isVisible={supportOverlayIsVisible}
        title="Feedback & Support"
        content={
          <p>
            We're still in beta so we are VERY open to feedback. If you have a question or there's a
            bug that we missed. Please let us know so that we can fix it!
          </p>
        }
        submitButtonText="Send"
        data={{
          collectionName: 'support',
          objectId: entertainer?.userId,
        }}
        onClose={() => setSupportOverlayIsVisible(false)}
      />
    </Screen>
  );
});
